import { useTranslation } from 'react-i18next';
import { type ResourceTypeEntity } from '../api/resourceService';
import { useState, useMemo } from 'react';

export default function ResourceTypesModal({
  resourceTypes,
  onCancel,
  onDelete,
  onOpenCreate,
  onOpenEdit,
}: {
  resourceTypes: ResourceTypeEntity[];
  onCancel: () => void;
  onDelete?: (resourceType: ResourceTypeEntity) => void;
  onOpenCreate?: () => void;
  onOpenEdit?: (resourceType: ResourceTypeEntity) => void;
}) {
  const { i18n } = useTranslation();
  const { t } = useTranslation();
  const lang = i18n.language.startsWith('en') ? 'en' : 'es';
  const [searchTerm, setSearchTerm] = useState('');

  // Filtrar tipos de recursos basado en el término de búsqueda
  const filteredResourceTypes = useMemo(() => {
    if (!searchTerm.trim()) return resourceTypes;
    
    const searchLower = searchTerm.toLowerCase();
    return resourceTypes.filter(resourceType => 
      resourceType.name[lang].toLowerCase().includes(searchLower) ||
      resourceType.id.toLowerCase().includes(searchLower)
    );
  }, [resourceTypes, searchTerm, lang]);

  const openCreate = () => {
    onOpenCreate?.();
  };

  const openEdit = (resourceType: ResourceTypeEntity) => {
    onOpenEdit?.(resourceType);
  };

  return (
    <div className="w-full max-w-4xl mx-auto h-full flex flex-col">
      {/* Header del modal - STICKY ARRIBA */}
      <div className="sticky top-0 bg-[var(--surface)] z-10 p-6 pb-4 border-b border-[var(--border)]">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-3xl font-bold text-[var(--text-default)] mb-2">
              {t('resourcePage.resourceTypesModal.title')}
            </h2>
            <p className="text-[var(--text-muted)]">
              {t('resourcePage.resourceTypesModal.subtitle')}
            </p>
          </div>
        </div>

        {/* Barra de búsqueda */}
        <div className="relative">
          <span className="material-icons absolute left-3 top-3 text-[var(--text-muted)] text-lg pointer-events-none">
            search
          </span>
          <input
            className="w-full pl-10 pr-4 py-3 border border-[var(--border)] rounded-lg bg-[var(--surface)] text-[var(--text-default)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--border)] focus:border-transparent transition-all duration-200"
            placeholder={t('resourcePage.resourceTypesModal.searchPlaceholder')}
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Contenido principal */}
      <div className="flex-1 overflow-y-auto max-h-[400px] sm:max-h-[500px] md:max-h-[600px] lg:max-h-[500px] xl:max-h-[600px]  min-h-[400px] sm:min-h-[500px] md:min-h-[600px] lg:min-h-[500px] xl:min-h-[600px]">
        <div className="p-6">
          {/* Lista de tipos de recursos existentes */}
          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-6 ">
            {filteredResourceTypes.length > 0 ? (
              <div className="space-y-4">
                {filteredResourceTypes.map(resourceType => (
                  <div
                    key={resourceType.id}
                    className="flex items-center justify-between p-4 bg-[var(--surface)] border border-[var(--border)] rounded-lg hover:bg-[var(--border)] hover:text-[var(--surface)] transition-colors group"
                  >
                    <div className="flex-1">
                      <h3 className="font-semibold text-[var(--text-default)] mb-1 group-hover:text-[var(--surface)] transition-colors">
                        {resourceType.name[lang]}
                      </h3>
                      <div className="flex items-center gap-4 text-sm text-[var(--text-muted)] group-hover:text-[var(--surface)] transition-colors">
                        <span>ID: {resourceType.id}</span>
                        <span>Recursos: {resourceType.resourceCount}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openEdit(resourceType)}
                        className="p-2 text-[var(--text-muted)] group-hover:text-[var(--surface)] hover:text-[var(--surface)] hover:bg-[var(--border)] rounded-lg transition-colors duration-200"
                        aria-label="Editar tipo de recurso"
                      >
                        <span className="material-icons text-lg">edit</span>
                      </button>
                      <button
                        onClick={() => onDelete?.(resourceType)}
                        className="p-2 text-[var(--text-muted)] group-hover:text-[var(--surface)] hover:text-[var(--surface)] hover:bg-[var(--border)] rounded-lg transition-colors duration-200"
                        aria-label="Eliminar tipo de recurso"
                      >
                        <span className="material-icons text-lg">delete</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <span className="material-icons text-6xl text-[var(--text-muted)] mb-4 block">
                  {searchTerm.trim() ? 'search_off' : 'category'}
                </span>
                <p className="text-[var(--text-muted)] text-lg">
                  {searchTerm.trim() 
                    ? t('resourcePage.resourceTypesModal.noResults', { searchTerm })
                    : t('resourcePage.resourceTypesModal.noResourceTypes')
                  }
                </p>
                <p className="text-sm text-[var(--text-muted)] mt-2">
                  {searchTerm.trim() 
                    ? t('resourcePage.resourceTypesModal.tryAnotherSearch')
                    : t('resourcePage.resourceTypesModal.createFirstResourceType')
                  }
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer con botones - STICKY ABAJO */}
      <div className="sticky bottom-0 bg-[var(--surface)] z-10 p-6 pt-4 border-t border-[var(--border)]">
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 sm:flex-none px-6 py-3 border border-[var(--border)] rounded-lg bg-[var(--surface)] text-[var(--text-default)] hover:bg-[var(--border)] hover:text-[var(--surface)] transition-colors duration-200 font-medium"
          >
            {t('resourcePage.resourceTypesModal.cancel')}
          </button>
          {onOpenCreate && (
            <button
              type="button"
              onClick={openCreate}
              className="flex-1 sm:flex-none px-6 py-3 bg-[var(--text-default)] text-[var(--surface)] rounded-lg hover:bg-[var(--border)] transition-colors duration-200 font-medium flex items-center justify-center gap-2"
            >
              <span className="material-icons text-lg">add</span>
              {t('resourcePage.resourceTypesModal.addResourceType')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
} 